import { useEffect, type Dispatch, type SetStateAction } from 'react'
import type { TimelineElement } from '../../context/TimelineContext'
import type { useRecurringLens } from '../../context/recurringLensContextCore'

export function useRecurringPageSubscription({
  recurringLens,
  item,
  setFocusedPageRefreshToken,
}: {
  recurringLens: ReturnType<typeof useRecurringLens>
  item: TimelineElement
  setFocusedPageRefreshToken: Dispatch<SetStateAction<number>>
}) {
  // Under a recurring lens, subscribe to the focused book page so ticks
  // made from other sessions (or server writers like fieldPass /
  // deltaProcessor) surface here without a full page reload.
  // TimelineContext's book-model subscription only watches TODAY's page,
  // so this is the only real-time signal for past/future occurrences.
  useEffect(() => {
    if (!recurringLens) return
    let unsub: (() => void) | undefined
    let disposed = false
    void (async () => {
      const [{ subscribeToPage }, { bookIdFor }] = await Promise.all([
        import('../../services/checklistBookService'),
        import('../../types/checklistBooks'),
      ])
      if (disposed) return
      const bookId = bookIdFor(item.workspaceId, recurringLens.mode)
      // Skip the very first snapshot — that's just the initial value the
      // one-shot loadBoardFromDb already projected. We only care about
      // remote changes after mount.
      let isFirstSnapshot = true
      unsub = subscribeToPage(bookId, recurringLens.focusedKey, () => {
        if (isFirstSnapshot) {
          isFirstSnapshot = false
          return
        }
        setFocusedPageRefreshToken((token) => token + 1)
      })
    })()
    return () => {
      disposed = true
      unsub?.()
    }
  }, [item.workspaceId, recurringLens?.focusedKey, recurringLens?.mode])
}
